import React, { useState, useMemo } from 'react';
import { TableContent } from './LayoutPage';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button, KIND, SIZE } from 'baseui/button';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Filter, Search, X } from 'lucide-react';
import { MantineDataTable } from '../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useBusinessPayments } from '../hooks/useBusinessPayments';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { Payment, PaymentStatus } from '../types/payment.types';
import { formatCardNumber, gatewayLabel, lastFour } from '../utils/paymentUtils';
import { PaymentActionsHost } from '../components/PaymentActionsHost';
import { paymentCurrency, refundSummary } from '../utils/refunds';
import { BaseDatePicker } from '../components/BaseDatePicker';
import { useBusinessContext } from '../context/BusinessContext';
import { useT, type MessageKey } from '../i18n/admin';

// ── Styled components matching AdminUsers/Products/Estates ──

const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 24px',
});

const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '320px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': { color: '#9CA3AF' },
});

const FiltersRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  padding: '0 24px 16px',
});

const FilterItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const FilterLabel = styled('label', {
  fontSize: '11px',
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

const Main = styled('div', {
  flex: 1,
  overflow: 'auto',
  padding: '0 24px 24px',
});

const StatusBadge = styled('span', ({ $status }: { $status: PaymentStatus }) => ({
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  backgroundColor:
    $status === 'success' ? '#D1FAE5' : $status === 'failed' ? '#FEE2E2' : $status === 'partially_refunded' ? '#FEF3C7' : '#F3F4F6',
  color:
    $status === 'success' ? '#065F46' : $status === 'failed' ? '#991B1B' : $status === 'partially_refunded' ? '#92400E' : '#374151',
}));

// Stored status → label key. The filter and the badge colours keep using the raw status.
const PAYMENT_STATUS_LABEL: Record<PaymentStatus, MessageKey> = {
  success: 'payments.status.success',
  failed: 'payments.status.failed',
  voided: 'payments.status.voided',
  partially_refunded: 'payments.status.partially_refunded',
  refunded: 'payments.status.refunded',
};
const STATUS_IDS = Object.keys(PAYMENT_STATUS_LABEL) as PaymentStatus[];

const MonospaceText = styled('span', {
  fontFamily: 'monospace',
  fontSize: '13px',
});

const GatewayBadge = styled('span', ({ $gateway }: { $gateway: 'portal' | 'carnet' }) => ({
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  backgroundColor: $gateway === 'portal' ? '#EDE9FE' : '#DBEAFE',
  color: $gateway === 'portal' ? '#5B21B6' : '#1E40AF',
}));

export const Payments = () => {
  const { business } = useBusinessContext();
  const { isAdmin } = useUserPermissions();
  const { data, isLoading } = useBusinessPayments();
  const { t, fmt, lang } = useT();
  const money = (amount: number | null | undefined, currency: string) => (amount === null || amount === undefined ? t('common.notAvailable') : fmt.money(amount, currency));

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<Array<{ id: string; label: string }>>([]);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Drawer state
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter and search logic
  const filteredPayments = useMemo(() => {
    if (!data?.payments) return [];

    let filtered = [...data.payments];

    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((payment) => {
        const ref = payment.payment_reference?.toLowerCase() || '';
        const txnRef = payment.transaction_reference?.toLowerCase() || '';
        const card = lastFour(payment.cardLast4).toLowerCase();
        const biz = payment.business_name?.toLowerCase() || '';
        const status = payment.payment_status?.toLowerCase() || '';
        const amount = payment.payment_amount?.toString() || '';
        return ref.includes(query) || txnRef.includes(query) || card.includes(query) || biz.includes(query) || status.includes(query) || amount.includes(query);
      });
    }

    if (statusFilter.length > 0) {
      const statuses = statusFilter.map((s) => s.id);
      filtered = filtered.filter((payment) => payment.payment_status && statuses.includes(payment.payment_status));
    }

    if (dateRange.from) {
      filtered = filtered.filter((payment) => {
        if (!payment.payment_date) return false;
        return new Date(payment.payment_date) >= dateRange.from!;
      });
    }
    if (dateRange.to) {
      filtered = filtered.filter((payment) => {
        if (!payment.payment_date) return false;
        return new Date(payment.payment_date) <= dateRange.to!;
      });
    }

    if (minAmount) {
      const min = parseFloat(minAmount);
      if (!isNaN(min)) filtered = filtered.filter((p) => p.payment_amount !== null && p.payment_amount >= min);
    }
    if (maxAmount) {
      const max = parseFloat(maxAmount);
      if (!isNaN(max)) filtered = filtered.filter((p) => p.payment_amount !== null && p.payment_amount <= max);
    }

    return filtered;
  }, [data?.payments, searchQuery, statusFilter, dateRange, minAmount, maxAmount]);

  // Table columns
  const columns = useMemo<MRT_ColumnDef<Payment>[]>(() => {
    const cols: MRT_ColumnDef<Payment>[] = [
      {
        accessorKey: 'payment_reference',
        header: t('payments.col.reference'),
        size: 180,
        Cell: ({ cell }) => <MonospaceText>{cell.getValue<string | null>() || '—'}</MonospaceText>,
      },
      {
        accessorKey: 'transaction_reference',
        header: t('payments.col.txnRef'),
        size: 160,
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>();
          if (!val) return <span style={{ color: '#9CA3AF' }}>—</span>;
          return <MonospaceText title={val}>{val.length > 16 ? `${val.substring(0, 16)}…` : val}</MonospaceText>;
        },
      },
      {
        accessorKey: 'payment_date',
        header: t('payments.col.date'),
        size: 140,
        Cell: ({ cell }) => <span>{fmt.dateTime(cell.getValue<string | null>()) || t('common.notAvailable')}</span>,
      },
      {
        accessorKey: 'payment_amount',
        header: t('payments.col.amount'),
        size: 100,
        Cell: ({ cell, row }) => <span>{money(cell.getValue<number | null>(), paymentCurrency(row.original))}</span>,
      },
      {
        accessorKey: 'cardLast4',
        header: t('payments.col.card'),
        size: 100,
        Cell: ({ cell }) => {
          const last4 = cell.getValue<string | null>();
          return <span>{last4 ? formatCardNumber(last4) : t('common.notAvailable')}</span>;
        },
      },
      {
        accessorKey: 'payment_status',
        header: t('payments.col.status'),
        size: 110,
        Cell: ({ cell }) => {
          const status = cell.getValue<PaymentStatus | null>();
          if (!status) return <span style={{ color: '#9CA3AF' }}>—</span>;
          return <StatusBadge $status={status}>{PAYMENT_STATUS_LABEL[status] ? t(PAYMENT_STATUS_LABEL[status]) : status}</StatusBadge>;
        },
      },
      {
        id: 'refunded_amount',
        accessorFn: (row) => row.refunded_amount ?? 0,
        header: t('payments.col.refunded'),
        size: 110,
        Cell: ({ row }) => {
          const s = refundSummary(row.original);
          if (!s.refundedCents) return <span style={{ color: '#9CA3AF' }}>—</span>;
          return <span title={t('payments.remaining', { amount: money(s.remainingCents / 100, s.currency) })}>{money(s.refundedCents / 100, s.currency)}</span>;
        },
      },
      {
        accessorKey: 'payment_gateway',
        header: t('payments.col.gateway'),
        size: 100,
        Cell: ({ cell }) => {
          const gw = cell.getValue<'portal' | 'carnet' | null>() || 'carnet';
          return <GatewayBadge $gateway={gw}>{/* i18n-ignore: gateway names */}
          {gatewayLabel(gw)}</GatewayBadge>;
        },
      },
    ];

    if (isAdmin) {
      cols.unshift({
        accessorKey: 'business_name',
        header: t('payments.col.business'),
        size: 160,
        Cell: ({ cell }) => <span style={{ fontWeight: 500 }}>{cell.getValue<string | null>() || '—'}</span>,
      });
    }

    return cols;
  }, [isAdmin, lang]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setDateRange({});
    setMinAmount('');
    setMaxAmount('');
  };

  const hasActiveFilters = searchQuery || statusFilter.length > 0 || dateRange.from || dateRange.to || minAmount || maxAmount;

  return (
    <TableContent>
      <Container>
        <Header>
          <HeadingMedium margin={0}>{t('payments.title')}</HeadingMedium>
          <Button
            size={SIZE.compact}
            kind={KIND.secondary}
            startEnhancer={() => <Filter size={16} />}
            onClick={() => setShowFilters(!showFilters)}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: showFilters ? 'var(--tenant-primary, #111827)' : '#F3F4F6',
                  color: showFilters ? '#fff' : '#374151',
                  borderRadius: '8px',
                  ':hover': {
                    backgroundColor: showFilters ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#E5E7EB',
                  },
                },
              },
            }}
          >
            {t('payments.filters')}
          </Button>
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <Search size={16} color="#9CA3AF" />
            <SearchInputEl
              placeholder={t('payments.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>

          {hasActiveFilters && (
            <Button
              size={SIZE.mini}
              kind={KIND.tertiary}
              onClick={handleClearFilters}
              startEnhancer={() => <X size={14} />}
              overrides={{
                BaseButton: {
                  style: {
                    color: '#6B7280',
                    fontSize: '13px',
                  },
                },
              }}
            >
              {t('payments.clear')}
            </Button>
          )}
        </ToolbarRow>

        {showFilters && (
          <FiltersRow>
            <FilterItem>
              <FilterLabel>{t('payments.filter.status')}</FilterLabel>
              <Select
                options={STATUS_IDS.map((id) => ({ id, label: t(PAYMENT_STATUS_LABEL[id]) }))}
                value={statusFilter.map((f) => ({ id: f.id, label: t(PAYMENT_STATUS_LABEL[f.id as PaymentStatus] ?? 'payments.filter.all') }))}
                onChange={(params) => setStatusFilter(params.value as any)}
                placeholder={t('payments.filter.all')}
                multi
                clearable
                size="compact"
              />
            </FilterItem>

            <FilterItem>
              <FilterLabel>{t('payments.filter.dateRange')}</FilterLabel>
              <BaseDatePicker
                mode="range"
                rangeValue={dateRange}
                onRangeChange={(range) => setDateRange(range || {})}
                placeholder={t('payments.filter.selectRange')}
              />
            </FilterItem>

            <FilterItem style={{ minWidth: '120px' }}>
              <FilterLabel>{t('payments.filter.minAmount')}</FilterLabel>
              <Input
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.currentTarget.value)}
                placeholder="0.00"
                startEnhancer="$"
                size="compact"
              />
            </FilterItem>

            <FilterItem style={{ minWidth: '120px' }}>
              <FilterLabel>{t('payments.filter.maxAmount')}</FilterLabel>
              <Input
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.currentTarget.value)}
                placeholder="0.00"
                startEnhancer="$"
                size="compact"
              />
            </FilterItem>
          </FiltersRow>
        )}

        <Main>
          <MantineDataTable
            columns={columns}
            data={filteredPayments}
            loading={isLoading}
            enablePagination
            enableSorting
            enableGlobalFilter={false}
            initialPageSize={20}
            pageSizeOptions={[10, 20, 50, 100]}
            onRowClick={(payment) => {
              setSelectedPayment(payment);
              setIsModalOpen(true);
            }}
          />
        </Main>
      </Container>

      <PaymentActionsHost
        payment={isModalOpen ? selectedPayment : null}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPayment(null);
        }}
        selectedBusinessId={business?.id}
      />
    </TableContent>
  );
};
